import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth } from '@/App';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { 
  Upload, 
  MapPin, 
  Camera, 
  X, 
  Loader2,
  CheckCircle
} from 'lucide-react';

const RegisterPetPage = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    nome: '',
    especie: '',
    raca: '',
    porte: '',
    descricao: '',
    status: '',
    latitude: '',
    longitude: '',
    ponto_referencia: ''
  });

  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [gettingLocation, setGettingLocation] = useState(false);

  const [searchingAddress, setSearchingAddress] = useState(false);
  const [addressSearch, setAddressSearch] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error('Por favor, selecione apenas arquivos de imagem');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error('A imagem deve ter no máximo 5MB');
        return;
      }
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const removeImage = () => {
    setImageFile(null);
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      toast.error('Seu navegador não suporta geolocalização');
      return;
    }

    setGettingLocation(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setFormData(prev => ({
          ...prev,
          latitude: position.coords.latitude.toString(),
          longitude: position.coords.longitude.toString()
        }));
        toast.success('Localização obtida com sucesso!');
        setGettingLocation(false);
      },
      (error) => {
        console.error('Geolocation error:', error);
        setGettingLocation(false);
        
        // Mensagens específicas para cada tipo de erro
        switch(error.code) {
          case error.PERMISSION_DENIED:
            toast.error('Permissão de localização negada. Por favor, permita o acesso à localização no seu navegador.');
            break;
          case error.POSITION_UNAVAILABLE:
            toast.error('Localização indisponível. Verifique se o GPS está ativado.');
            break;
          case error.TIMEOUT:
            toast.error('Tempo esgotado ao obter localização. Tente novamente.');
            break;
          default:
            toast.error('Não foi possível obter sua localização. Digite manualmente as coordenadas.');
        }
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  };

  // Buscar coordenadas pelo endereço usando Nominatim (OpenStreetMap)
  const searchByAddress = async () => {
    if (!addressSearch.trim()) {
      toast.error('Digite um endereço para buscar');
      return;
    }

    setSearchingAddress(true);
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(addressSearch)}&limit=1`
      );
      const data = await response.json();
      
      if (data && data.length > 0) {
        setFormData(prev => ({
          ...prev,
          latitude: data[0].lat,
          longitude: data[0].lon
        }));
        toast.success(`Localização encontrada: ${data[0].display_name.substring(0, 50)}...`);
      } else {
        toast.error('Endereço não encontrado. Tente ser mais específico.');
      }
    } catch (error) {
      console.error('Address search error:', error);
      toast.error('Erro ao buscar endereço. Tente novamente.');
    } finally {
      setSearchingAddress(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.nome || !formData.especie || !formData.porte || 
        !formData.descricao || !formData.status || !formData.latitude || !formData.longitude) {
      toast.error('Por favor, preencha todos os campos obrigatórios');
      return;
    }

    if (formData.descricao.length < 10) {
      toast.error('A descrição deve ter pelo menos 10 caracteres');
      return;
    }

    setLoading(true);

    try {
      const submitData = new FormData();
      submitData.append('nome', formData.nome);
      submitData.append('especie', formData.especie);
      submitData.append('raca', formData.raca || '');
      submitData.append('porte', formData.porte);
      submitData.append('descricao', formData.descricao);
      submitData.append('status', formData.status);
      submitData.append('latitude', formData.latitude);
      submitData.append('longitude', formData.longitude);
      submitData.append('ponto_referencia', formData.ponto_referencia || '');
      
      if (imageFile) {
        submitData.append('imagem', imageFile);
      }

      await axios.post(`${API}/pets`, submitData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success('Pet cadastrado com sucesso!');
      navigate('/meus-pets');
    } catch (error) {
      console.error('Error creating pet:', error);
      const message = error.response?.data?.detail || 'Erro ao cadastrar pet';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F5D79C]">
      <Navbar />
      
      <main className="pt-24 pb-16 px-4">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="logo-container w-20 h-20 mx-auto flex items-center justify-center mb-4">
              <span className="text-4xl">🐾</span>
            </div>
            <h1 className="text-3xl font-display font-bold text-[#5D4A3A] mb-2">Cadastrar Pet</h1>
            <p className="text-[#7A5B3E]">Preencha as informações do pet para ajudá-lo a ser encontrado</p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="bg-[#FCEBC5] rounded-2xl shadow-lg p-6 md:p-8 space-y-6 border border-[#C4956A]">
            {/* Image Upload */}
            <div>
              <Label className="text-[#5D4A3A] font-medium">Foto do Pet</Label>
              <div className="mt-2">
                {imagePreview ? (
                  <div className="relative w-full h-64 rounded-xl overflow-hidden border border-[#C4956A]">
                    <img 
                      src={imagePreview} 
                      alt="Preview" 
                      className="w-full h-full object-cover"
                    />
                    <button
                      type="button"
                      onClick={removeImage}
                      className="absolute top-3 right-3 w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                ) : (
                  <div 
                    onClick={() => fileInputRef.current?.click()}
                    className="w-full h-64 border-2 border-dashed border-[#C4956A] rounded-xl flex flex-col items-center justify-center cursor-pointer hover:bg-[#F5E6C8] transition-colors"
                  >
                    <Camera className="w-12 h-12 text-[#7A5B3E] mb-3" />
                    <p className="text-[#5D4A3A] font-medium">Clique para adicionar uma foto</p>
                    <p className="text-sm text-[#7A5B3E] mt-1">JPG, PNG ou GIF (máx. 5MB)</p>
                  </div>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="hidden"
                  data-testid="image-input"
                />
              </div>
              <p className="text-xs text-[#6B5344] mt-2">
                ⚠️ As fotos são automaticamente removidas após 7 dias
              </p>
            </div>

            {/* Basic Info */}
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="nome" className="text-[#5D4A3A] font-medium">Nome do Pet *</Label>
                <Input
                  id="nome"
                  name="nome"
                  value={formData.nome}
                  onChange={handleChange}
                  placeholder="Ex: Rex, Mimi..."
                  className="input-original mt-1"
                  data-testid="input-nome"
                  required
                />
              </div>
              <div>
                <Label htmlFor="especie" className="text-[#5D4A3A] font-medium">Espécie *</Label>
                <Input
                  id="especie"
                  name="especie"
                  value={formData.especie}
                  onChange={handleChange}
                  placeholder="Ex: Cachorro, Gato..."
                  className="input-original mt-1"
                  data-testid="input-especie"
                  required
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="raca" className="text-[#5D4A3A] font-medium">Raça</Label>
                <Input
                  id="raca"
                  name="raca"
                  value={formData.raca}
                  onChange={handleChange}
                  placeholder="Ex: Labrador, Siamese..."
                  className="input-original mt-1"
                  data-testid="input-raca"
                />
              </div>
              <div>
                <Label className="text-[#5D4A3A] font-medium">Porte *</Label>
                <Select
                  value={formData.porte}
                  onValueChange={(value) => handleSelectChange('porte', value)}
                >
                  <SelectTrigger className="mt-1 rounded-full border-[#C4956A] bg-[#F5E6C8] text-[#5D4A3A]" data-testid="select-porte">
                    <SelectValue placeholder="Selecione o porte" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#FCEBC5] border-[#C4956A]">
                    <SelectItem value="pequeno">Pequeno</SelectItem>
                    <SelectItem value="medio">Médio</SelectItem>
                    <SelectItem value="grande">Grande</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label className="text-[#5D4A3A] font-medium">Status *</Label>
              <Select
                value={formData.status}
                onValueChange={(value) => handleSelectChange('status', value)}
              >
                <SelectTrigger className="mt-1 rounded-full border-[#C4956A] bg-[#F5E6C8] text-[#5D4A3A]" data-testid="select-status">
                  <SelectValue placeholder="Qual a situação do pet?" />
                </SelectTrigger>
                <SelectContent className="bg-[#FCEBC5] border-[#C4956A]">
                  <SelectItem value="perdido">Perdido - Estou procurando</SelectItem>
                  <SelectItem value="encontrado">Encontrado - Achei este pet</SelectItem>
                  <SelectItem value="adocao">Para Adoção</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="descricao" className="text-[#5D4A3A] font-medium">Descrição *</Label>
              <Textarea
                id="descricao"
                name="descricao"
                value={formData.descricao}
                onChange={handleChange}
                placeholder="Descreva características do pet, como cor, coleira, comportamento, último local visto..."
                className="mt-1 rounded-2xl border-[#C4956A] bg-[#F5E6C8] text-[#5D4A3A] min-h-[120px] focus:border-[#6B5344]"
                data-testid="input-descricao"
                required
              />
            </div>

            {/* Location */}
            <div className="border-t border-[#C4956A] pt-6">
              <div className="flex items-center justify-between mb-4">
                <Label className="text-[#5D4A3A] font-medium flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-[#6B5344]" />
                  Localização *
                </Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={getCurrentLocation}
                  disabled={gettingLocation}
                  className="rounded-full border-[#C4956A] text-[#6B5344] hover:bg-[#F5E6C8]"
                >
                  {gettingLocation ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Obtendo...</>
                  ) : (
                    <><MapPin className="w-4 h-4 mr-2" /> Usar GPS</>
                  )}
                </Button>
              </div>

              {/* Buscar por endereço */}
              <div className="mb-4">
                <Label className="text-sm text-[#7A5B3E] mb-2 block">Ou busque pelo endereço:</Label>
                <div className="flex gap-2">
                  <Input
                    value={addressSearch}
                    onChange={(e) => setAddressSearch(e.target.value)}
                    placeholder="Ex: Praça da Sé, São Paulo, SP"
                    className="input-original flex-1"
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), searchByAddress())}
                  />
                  <Button
                    type="button"
                    onClick={searchByAddress}
                    disabled={searchingAddress}
                    className="btn-primary px-4"
                  >
                    {searchingAddress ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      'Buscar'
                    )}
                  </Button>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="latitude" className="text-sm text-[#7A5B3E]">Latitude</Label>
                  <Input
                    id="latitude"
                    name="latitude"
                    type="number"
                    step="any"
                    value={formData.latitude}
                    onChange={handleChange}
                    placeholder="-23.5505"
                    className="input-original mt-1"
                    data-testid="input-latitude"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="longitude" className="text-sm text-[#7A5B3E]">Longitude</Label>
                  <Input
                    id="longitude"
                    name="longitude"
                    type="number"
                    step="any"
                    value={formData.longitude}
                    onChange={handleChange}
                    placeholder="-46.6333"
                    className="input-original mt-1"
                    data-testid="input-longitude"
                    required
                  />
                </div>
              </div>

              <div className="mt-4">
                <Label htmlFor="ponto_referencia" className="text-[#5D4A3A] font-medium">Ponto de Referência</Label>
                <Input
                  id="ponto_referencia"
                  name="ponto_referencia"
                  value={formData.ponto_referencia}
                  onChange={handleChange}
                  placeholder="Ex: Próximo ao shopping, Praça central..."
                  className="input-original mt-1"
                  data-testid="input-referencia"
                />
              </div>
            </div>

            {/* Submit */}
            <Button
              type="submit"
              disabled={loading}
              className="w-full btn-primary py-6 text-lg font-semibold uppercase tracking-wide"
              data-testid="submit-button"
            >
              {loading ? (
                <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Cadastrando...</>
              ) : (
                <><CheckCircle className="w-5 h-5 mr-2" /> Cadastrar Pet</>
              )}
            </Button>
          </form>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default RegisterPetPage;
